import { canTransition } from './state.js';
import type { VariantState } from './state.js';

// ---------------------------------------------------------------------------
// Pure predicates over the domain's own transition table -- the single
// source of truth both the MCP tool surface
// (apps/worker/src/mcp/tools.ts's `computeNextActions`) and the web review
// screen (apps/web/src/routes/review.tsx, .../components/SchedulePanel.tsx)
// read to decide which actions to offer a caller.
//
// Each predicate names the exact guard the matching use case enforces (see
// packages/application/src/{review,schedule}/*.ts) -- keeping the guard and
// its UI-facing predicate side by side here is what stops a UI from
// silently drifting out of sync with a use case's own precondition. That
// drift is exactly what the whole-branch review's C2 finding was: the
// review screen computed "can approve" from validation issues alone, never
// consulting the variant's actual state, so a `published` variant still
// rendered an enabled Approve button and an unconditional "this publishes
// the post" banner.
// ---------------------------------------------------------------------------

/** upsertVariant's only state-based guard (upsert-variant.ts): refuses exactly `published`/`publishing`. */
export const canUpsertVariant = (state: VariantState): boolean =>
  state !== 'published' && state !== 'publishing';

/** submitForReview calls `transition(state, 'needs_review')` verbatim (submit-for-review.ts). */
export const canSubmitForReview = (state: VariantState): boolean => canTransition(state, 'needs_review');

/** approveVariant calls `transition(state, 'approved')` verbatim (approve-variant.ts) -- true only from `needs_review`. */
export const canApproveVariant = (state: VariantState): boolean => canTransition(state, 'approved');

/** requestChanges calls `transition(state, 'draft')` verbatim (request-changes.ts). */
export const canRequestChanges = (state: VariantState): boolean => canTransition(state, 'draft');

/**
 * scheduleVariant's actual precondition (schedule-variant.ts): requires an
 * approved revision, and either re-schedules in place when already
 * `scheduled` (the one case its own `if (variant.state !== 'scheduled')`
 * guard skips `transition()` entirely) or otherwise requires `canTransition`
 * to succeed -- which a `canceled` variant that kept its approval satisfies
 * (`canceled: ['scheduled', 'draft']`), and a `needs_review` variant does
 * not. Gating only on `approvedRevisionId !== null`, as the schedule panel
 * used to, is wrong for exactly the states this second clause excludes.
 */
export const canScheduleVariant = (state: VariantState, approvedRevisionId: string | null): boolean =>
  approvedRevisionId !== null && (state === 'scheduled' || canTransition(state, 'scheduled'));
